
import React, { useState } from 'react';
import { useQuestionBank, QuestionBankItem } from '@/hooks/useQuestionBank';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Loader2, Plus, Search, ChevronDown, FileText, List, Mic, Video, Code } from "lucide-react";
import { Question, QuestionType } from "@/types/test";
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";

interface QuestionBankProps {
  onSelectQuestion: (question: Question) => void;
}

export const QuestionBank: React.FC<QuestionBankProps> = ({ onSelectQuestion }) => {
  const [filters, setFilters] = useState({
    category: 'all',
    difficulty: 'all',
    question_type: 'all' as QuestionType | 'all',
    searchQuery: '',
    skill_tags: [] as string[],
  });

  const { data: questions, isLoading, error } = useQuestionBank(filters);

  const handleAddQuestion = (bankItem: QuestionBankItem) => {
    // Convert question bank item to test question format
    const newQuestion: Question = {
      id: crypto.randomUUID(),
      question_type: bankItem.question_type,
      content: bankItem.content,
      time_limit: bankItem.time_limit,
      order: 0, // Will be set by parent component
      marks: bankItem.marks,
      // Add new fields with defaults
      skill: bankItem.skill_tags?.[0] || null,
      difficulty: (bankItem.difficulty?.toLowerCase() === 'beginner' ? 'easy' : 
                 bankItem.difficulty?.toLowerCase() === 'intermediate' ? 'medium' : 
                 bankItem.difficulty?.toLowerCase() === 'advanced' ? 'hard' : null) as 'easy' | 'medium' | 'hard' | null,
      markscheme: null
    };
    
    console.log('Adding question from bank:', newQuestion);
    onSelectQuestion(newQuestion);
  };

  const handleFilterChange = (key: keyof typeof filters, value: any) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  // Get question type icon
  const getQuestionTypeIcon = (type: QuestionType) => {
    switch(type) {
      case 'multiple_choice':
        return <List className="h-5 w-5 text-blue-500" />;
      case 'essay':
        return <FileText className="h-5 w-5 text-green-500" />;
      case 'audio_response':
        return <Mic className="h-5 w-5 text-amber-500" />;
      case 'video_response':
        return <Video className="h-5 w-5 text-red-500" />;
      case 'coding':
        return <Code className="h-5 w-5 text-purple-500" />;
      default:
        return <FileText className="h-5 w-5" />;
    }
  };

  // Get difficulty color class
  const getDifficultyColorClass = (difficulty: string) => {
    switch(difficulty) {
      case 'Beginner':
        return 'text-green-500 border-green-500';
      case 'Intermediate':
        return 'text-yellow-500 border-yellow-500';
      case 'Advanced':
        return 'text-red-500 border-red-500';
      default:
        return 'text-muted-foreground border-muted-foreground';
    }
  };

  return (
    <div className="flex flex-col md:flex-row h-full">
      {/* Filters Column - Left side */}
      <div className="w-full md:w-72 lg:w-80 flex-shrink-0 space-y-3 md:border-r md:pr-4 pb-2 md:pb-0 md:h-full overflow-y-auto">
        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search questions..."
            value={filters.searchQuery}
            onChange={(e) => handleFilterChange('searchQuery', e.target.value)}
            className="pl-8"
          />
        </div>
        
        <div className="space-y-3">
          <div>
            <label className="text-sm font-medium mb-1 block text-muted-foreground">Category</label>
            <Select
              value={filters.category}
              onValueChange={(value) => handleFilterChange('category', value)}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                <SelectItem value="Technical">Technical</SelectItem>
                <SelectItem value="Behavioral">Behavioral</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div>
            <label className="text-sm font-medium mb-1 block text-muted-foreground">Difficulty</label>
            <Select
              value={filters.difficulty}
              onValueChange={(value) => handleFilterChange('difficulty', value)}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Difficulty" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Difficulties</SelectItem>
                <SelectItem value="Beginner">Beginner</SelectItem>
                <SelectItem value="Intermediate">Intermediate</SelectItem>
                <SelectItem value="Advanced">Advanced</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div>
            <label className="text-sm font-medium mb-1 block text-muted-foreground">Question Type</label>
            <Select
              value={filters.question_type}
              onValueChange={(value) => handleFilterChange('question_type', value as QuestionType | 'all')}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Question Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="multiple_choice">Multiple Choice</SelectItem>
                <SelectItem value="essay">Essay</SelectItem>
                <SelectItem value="audio_response">Audio</SelectItem>
                <SelectItem value="video_response">Video</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>
      
      {/* Questions List - Right side */}
      <div className="flex-1 overflow-y-auto md:h-full md:pl-4 md:pt-0 pt-3">
        {isLoading ? (
          <div className="flex justify-center items-center h-40">
            <Loader2 className="h-8 w-8 animate-spin text-foreground/70" />
          </div>
        ) : error ? (
          <div className="text-center text-destructive p-4">
            Error loading question bank. Please try again.
          </div>
        ) : questions?.length === 0 ? (
          <div className="text-center text-muted-foreground p-6 bg-card rounded-md border border-border">
            No questions found. Try adjusting your filters.
          </div>
        ) : (
          <div className="space-y-2">
            {questions?.map((question) => (
              <Card key={question.id} className="bg-card shadow-sm hover:shadow transition-shadow">
                <div className="py-2 px-3">
                  {/* Two row layout */}
                  <div className="flex flex-col space-y-1.5">
                    {/* First row: Icon and question text */}
                    <div className="flex items-center gap-3">
                      <div className="flex-shrink-0">
                        {getQuestionTypeIcon(question.question_type)}
                      </div>
                      
                      {question.question_type === 'multiple_choice' ? (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" className="flex items-center justify-start gap-1 h-auto py-1 px-1.5 hover:bg-background/80 w-auto max-w-full">
                              <span className="truncate text-sm font-normal text-left">
                                {question.content.question?.slice(0, 100)}
                                {question.content.question?.length > 100 ? '...' : ''}
                              </span>
                              <ChevronDown className="h-4 w-4 flex-shrink-0" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="start" className="w-[350px] p-2">
                            <div className="p-2">
                              <p className="font-medium mb-2">{question.content.question}</p>
                              <ul className="space-y-1">
                                {question.content.options?.map((option, index) => (
                                  <li key={index} className={
                                    question.content.correctOption === index 
                                      ? 'text-sm text-green-600 font-medium' 
                                      : 'text-sm text-muted-foreground'
                                  }>
                                    {option}
                                    {question.content.correctOption === index && ' (✓)'}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      ) : (
                        <span className="truncate text-sm">
                          {question.content.question?.slice(0, 100)}
                          {question.content.question?.length > 100 ? '...' : ''}
                        </span>
                      )}
                    </div>
                    
                    {/* Second row: Metadata and action button */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className={`text-xs ${getDifficultyColorClass(question.difficulty)}`}>
                          {question.difficulty}
                        </Badge>
                        
                        {question.marks && (
                          <Badge variant="secondary" className="text-xs">
                            {question.marks} mark{question.marks !== 1 ? 's' : ''}
                          </Badge>
                        )}
                        
                        {question.time_limit && (
                          <Badge variant="outline" className="text-xs">
                            {question.time_limit}s
                          </Badge>
                        )}
                      </div>
                      
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => handleAddQuestion(question)}
                        className="whitespace-nowrap"
                      >
                        <Plus className="mr-1 h-4 w-4" />
                        Add
                      </Button>
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
